import type { IdentifierType } from "@auth0/auth0-acul-js";

export type IdentifierFieldConfig = {
  type: IdentifierType;
  name: "email" | "username" | "phoneNumber";
  label: string;
  placeholder: string;
  autoComplete: string;
  inputType: string;
  required: boolean;
};

export const buildIdentifierFields = (
  requiredIdentifiers: IdentifierType[] | null | undefined,
  optionalIdentifiers: IdentifierType[] | null | undefined,
  texts?: Record<string, string> | null
): IdentifierFieldConfig[] => {
  const identifierMeta: Record<
    IdentifierType,
    Omit<IdentifierFieldConfig, "required">
  > = {
    email: {
      type: "email",
      name: "email",
      label: texts?.emailPlaceholder || "Email Address",
      placeholder: texts?.emailPlaceholder || "you@company.com",
      autoComplete: "email",
      inputType: "email",
    },
    username: {
      type: "username",
      name: "username",
      label: texts?.usernameOnlyPlaceholder || "Username",
      placeholder: texts?.usernameOnlyPlaceholder || "Choose a username",
      autoComplete: "username",
      inputType: "text",
    },
    phone: {
      type: "phone",
      name: "phoneNumber",
      label: texts?.phonePlaceholder || "Phone Number",
      placeholder: texts?.phonePlaceholder || "e.g. +1 555 0100",
      autoComplete: "tel",
      inputType: "tel",
    },
  } as const;

  const orderedIdentifiers: Array<{ value: IdentifierType; required: boolean }> = [
    ...(requiredIdentifiers || []).map((value) => ({ value, required: true })),
    ...(optionalIdentifiers || []).map((value) => ({ value, required: false })),
  ];

  if (orderedIdentifiers.length === 0) {
    orderedIdentifiers.push({ value: "email", required: true });
  }

  const seen = new Set<IdentifierType>();
  const fields: IdentifierFieldConfig[] = [];

  orderedIdentifiers.forEach(({ value, required }) => {
    if (seen.has(value)) {
      return;
    }
    const config = identifierMeta[value];
    if (config) {
      seen.add(value);
      fields.push({
        ...config,
        required,
        label: required ? `${config.label}*` : config.label,
      });
    }
  });

  return fields;
};

